// Group capture can add a full contact sheet at once. The soft budget drops
// flexible overflow first, while the hard ceiling bounds protected-slot growth
// by dropping the oldest protected references only as a last resort.
export const MAX_DRAFT_REFERENCES = 96;
export const MAX_TOTAL_DRAFT_REFERENCES = 200;
const MAX_ITEM_LENGTH = 160;
const MAX_REFERENCE_NOTE_LENGTH = 240;
const CREATIVE_BIBLE_ASSET_STATE_VERSION = 1;
const CREATIVE_BIBLE_ASSET_SECTIONS = [
  'characters',
  'logos',
  'palette',
  'colorSystem',
  'typography',
  'documents',
  'style',
  'misc',
];
const FLEXIBLE_ASSET_SECTIONS = new Set(['documents', 'misc']);

export const CREATIVE_BIBLE_ASSET_SECTION_KEYS = Object.freeze(CREATIVE_BIBLE_ASSET_SECTIONS);

function generateCreativeId(prefix = 'creative') {
  const now = Date.now();
  const rand = Math.random().toString(36).slice(2, 8);
  return `${prefix}_${now}_${rand}`;
}

function clampString(value, maxLength) {
  if (typeof value !== 'string') return '';
  return value.trim().slice(0, maxLength);
}

function normalizeReference(reference = {}, index = 0) {
  const base = {
    id: clampString(reference.id, 128) || generateCreativeId(`ref-${index}`),
    role: clampString(reference.role, 64),
    sourceKind: clampString(reference.sourceKind, 64) || 'mediaItem',
    mediaKind: clampString(reference.mediaKind, 64) || 'image',
    sourceId: clampString(reference.sourceId, 128) || null,
    takeId: clampString(reference.takeId, 128) || null,
    note: clampString(reference.note, MAX_REFERENCE_NOTE_LENGTH),
  };

  return {
    ...base,
    assetRef: reference.assetRef && typeof reference.assetRef === 'object'
      ? {
        storageKey: clampString(reference.assetRef.storageKey, 512) || null,
        url: clampString(reference.assetRef.url, 2048) || null,
      }
      : null,
    previewUrl: clampString(reference.previewUrl, 2048) || clampString(reference.url, 2048) || null,
    width: Number.isFinite(reference.width) ? reference.width : null,
    height: Number.isFinite(reference.height) ? reference.height : null,
    descriptor: clampString(reference.descriptor, MAX_ITEM_LENGTH) || null,
  };
}

function normalizeOrderBySection(orderBySection = {}) {
  if (!orderBySection || typeof orderBySection !== 'object' || Array.isArray(orderBySection)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(orderBySection).map(([sectionKey, ids]) => [
      sectionKey,
      Array.from(new Set((Array.isArray(ids) ? ids : []).filter(Boolean))),
    ])
  );
}

function createAssetRefsFromReference(reference = {}) {
  const normalizedReference = normalizeReference(reference);
  const mediaId = normalizedReference.sourceId || generateCreativeId('media');
  const setId = generateCreativeId('refset');

  return {
    items: [{
      mediaId,
      semanticRoles: [normalizedReference.role || 'reference'],
      tags: [],
      quality: {},
      semantics: {},
      createdAt: Date.now(),
      updatedAt: Date.now(),
      source: normalizedReference,
    }],
    sets: [{
      id: setId,
      kind: 'manual',
      label: 'Primary References',
      assetIds: [mediaId],
      promoted: false,
      status: 'ready',
      source: 'user',
      generator: null,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }],
    primarySetId: setId,
  };
}

export function createCreativeBibleAsset({
  id = generateCreativeId('creative-asset'),
  sectionKey = 'misc',
  name = '',
  summary = '',
  analysis = null,
  refs = {},
  links = {},
  contribution = {},
  createdAt = Date.now(),
  updatedAt = createdAt,
} = {}) {
  return {
    id,
    sectionKey: CREATIVE_BIBLE_ASSET_SECTIONS.includes(sectionKey) ? sectionKey : 'misc',
    name: clampString(name, MAX_ITEM_LENGTH),
    summary: clampString(summary, MAX_REFERENCE_NOTE_LENGTH),
    analysis: analysis && typeof analysis === 'object' ? analysis : null,
    refs: {
      items: Array.isArray(refs.items) ? refs.items : [],
      sets: Array.isArray(refs.sets) ? refs.sets : [],
      primarySetId: refs.primarySetId || refs.sets?.[0]?.id || null,
    },
    links: {
      canvasGroupId: links?.canvasGroupId || null,
    },
    contribution: {
      conceptKey: clampString(contribution?.conceptKey, 128) || null,
      snapshotPatch: contribution?.snapshotPatch && typeof contribution.snapshotPatch === 'object'
        ? contribution.snapshotPatch
        : null,
      confidence: Number.isFinite(contribution?.confidence) ? contribution.confidence : null,
      sourceMediaIds: Array.from(new Set((contribution?.sourceMediaIds || []).filter(Boolean))),
    },
    createdAt,
    updatedAt,
  };
}

export function normalizeCreativeBibleAsset(asset = {}, index = 0) {
  const createdAt = Number.isFinite(asset.createdAt) ? asset.createdAt : Date.now();
  const refs = asset?.refs?.items || asset?.refs?.sets
    ? asset.refs
    : createAssetRefsFromReference(asset);

  return createCreativeBibleAsset({
    id: clampString(asset.id, 128) || generateCreativeId(`creative-asset-${index}`),
    sectionKey: clampString(asset.sectionKey, 64) || 'misc',
    name: asset.name || asset.descriptor || asset.note || `Reference ${index + 1}`,
    summary: asset.summary || asset.note || '',
    analysis: asset.analysis,
    refs,
    links: asset.links,
    contribution: asset.contribution,
    createdAt,
    updatedAt: Number.isFinite(asset.updatedAt) ? asset.updatedAt : createdAt,
  });
}

function normalizeCreativeBibleAssetEntries(entries = []) {
  if (entries instanceof Map) {
    return Array.from(entries.values()).map((entry, index) => normalizeCreativeBibleAsset(entry, index));
  }

  if (Array.isArray(entries)) {
    if (entries.every((entry) => Array.isArray(entry) && entry.length >= 2)) {
      return entries.map(([, entry], index) => normalizeCreativeBibleAsset(entry, index));
    }
    return entries.map((entry, index) => normalizeCreativeBibleAsset(entry, index));
  }

  return [];
}

function isProtectedAssetSection(sectionKey) {
  return !FLEXIBLE_ASSET_SECTIONS.has(sectionKey);
}

function retainSlotAwareItems(
  items,
  isProtected,
  softLimit = MAX_DRAFT_REFERENCES,
  hardLimit = MAX_TOTAL_DRAFT_REFERENCES,
) {
  const sourceItems = Array.isArray(items) ? items : [];
  const protectedItems = sourceItems.filter(isProtected);
  const retainedProtectedItems = protectedItems.slice(Math.max(0, protectedItems.length - hardLimit));
  const flexibleItems = sourceItems.filter((item) => !isProtected(item));
  const flexibleBudget = Math.min(
    Math.max(0, softLimit - retainedProtectedItems.length),
    Math.max(0, hardLimit - retainedProtectedItems.length),
  );
  const retainedItems = new Set([
    ...retainedProtectedItems,
    ...flexibleItems.slice(0, flexibleBudget),
  ]);

  return sourceItems.filter((item) => retainedItems.has(item));
}

export function truncateCreativeBibleAssetEntries(entries = [], limit = MAX_DRAFT_REFERENCES) {
  const sourceEntries = Array.isArray(entries) ? entries : [];
  return retainSlotAwareItems(
    sourceEntries,
    (entry) => isProtectedAssetSection(entry?.sectionKey),
    limit,
  );
}

export function normalizeCreativeBibleAssetState(assetState = {}, legacyReferences = []) {
  const normalizedEntries = normalizeCreativeBibleAssetEntries(assetState.entries);
  const fallbackEntries = normalizedEntries.length > 0
    ? normalizedEntries
    : (Array.isArray(legacyReferences) ? legacyReferences.map((reference, index) => normalizeCreativeBibleAsset(reference, index)) : []);
  const entries = truncateCreativeBibleAssetEntries(fallbackEntries);
  const retainedEntryIds = new Set(entries.map((entry) => entry.id));
  const orderBySection = Object.fromEntries(
    Object.entries(normalizeOrderBySection(assetState.orderBySection)).map(([sectionKey, ids]) => [
      sectionKey,
      ids.filter((id) => retainedEntryIds.has(id)),
    ])
  );

  return {
    version: Number.isFinite(assetState.version) ? assetState.version : CREATIVE_BIBLE_ASSET_STATE_VERSION,
    entries,
    orderBySection,
  };
}

export function materializeCreativeBibleReferences(assetState = {}) {
  const orderedReferences = [];

  for (const asset of normalizeCreativeBibleAssetEntries(assetState.entries)) {
    const itemsByMediaId = new Map((asset.refs?.items || []).map((item) => [item.mediaId, item]));
    const seenMediaIds = new Set();
    const orderedMediaIds = [];

    for (const set of asset.refs?.sets || []) {
      for (const mediaId of set.assetIds || []) {
        if (!mediaId || seenMediaIds.has(mediaId)) continue;
        seenMediaIds.add(mediaId);
        orderedMediaIds.push(mediaId);
      }
    }

    for (const mediaId of orderedMediaIds) {
      const item = itemsByMediaId.get(mediaId);
      const source = item?.source || {};
      orderedReferences.push({
        protected: isProtectedAssetSection(asset.sectionKey),
        reference: normalizeReference({
          id: `${asset.id}:${mediaId}`,
          role: source.role || item?.semanticRoles?.[0] || 'reference',
          sourceKind: source.sourceKind || 'mediaItem',
          mediaKind: source.mediaKind || 'image',
          sourceId: source.sourceId || mediaId,
          takeId: source.takeId || null,
          note: source.note || asset.summary || '',
          assetRef: source.assetRef || null,
          previewUrl: source.previewUrl || null,
          width: source.width || null,
          height: source.height || null,
          descriptor: source.descriptor || asset.name || asset.summary || 'Reference',
        }),
      });
    }
  }

  return retainSlotAwareItems(
    orderedReferences,
    (entry) => entry.protected,
  ).map((entry) => entry.reference);
}
